import { api } from '../../lib/api.js'
import { isNetworkError } from '../../lib/savedCopy.js'
import { todayISO, addDays } from './lib.js'

/**
 * Session Builder with no service (Charles, 2026-10-14). The service worker
 * keeps each bookings answer by its exact address, but the calendar asks
 * for a different from–to almost every time it is opened (it starts on
 * today), so those copies alone rarely match. So one wide answer — the last
 * week and the coming six — is saved whenever there is a connection
 * (lib/offlineSync.js, at launch and at most every 15 minutes), and when a
 * range can't be fetched the days asked for are cut out of that one.
 */

const KEY = 'sa-offline-window'
const BACK = 7
const AHEAD = 42

const bookingsUrl = (from, to) => `/assistant/bookings?from=${from}&to=${to}`

function savedWindow() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || 'null')
  } catch {
    return null
  }
}

/** Fetch the wide answer, and the screen's names and colours, while there is service. */
export async function saveBuilderCopy() {
  const today = todayISO()
  const from = addDays(today, -BACK)
  const to = addDays(today, AHEAD)
  await api(bookingsUrl(from, to))
  try {
    localStorage.setItem(KEY, JSON.stringify({ from, to }))
  } catch {
    /* the exact-address copies still work */
  }
  await api('/assistant/calendars').catch(() => {})
  await api('/assistant/status').catch(() => {})
}

/**
 * The bookings for from–to: the server's answer, or with no service the
 * same days cut from the saved wide answer. `savedWindow` on the result
 * names the saved days when they don't cover everything asked for.
 */
export async function bookingsOrSaved(from, to) {
  try {
    return await api(bookingsUrl(from, to))
  } catch (err) {
    const w = savedWindow()
    if (!isNetworkError(err) || !w || w.to < from || w.from > to) throw err
    let all
    try {
      all = await api(bookingsUrl(w.from, w.to))
    } catch {
      throw err
    }
    const inRange = (d) => d >= from && d <= to
    return {
      ...all,
      from,
      to,
      items: (all.items || []).filter((it) => inRange(it.date)),
      allDay: (all.allDay || []).filter((a) => a.end >= from && a.start <= to),
      away: (all.away || []).filter((a) => inRange(a.date)),
      // A saved copy is what it is: don't re-ask every 3 s for feeds that
      // were still arriving when it was made.
      feedsLoading: false,
      feedsRefreshing: false,
      savedWindow: w.from > from || w.to < to ? w : null,
    }
  }
}
